/**
 * Znak Firmeto i ikony ekranów wejścia — rysunki z systemu projektowego.
 *
 * Trzymamy je jako komponenty, a nie plik SVG, bo wszystkie oprócz znaku dziedziczą kolor
 * po otoczeniu (`currentColor`): ta sama ikona jest miedziana na granacie i granatowa na bieli.
 */

type IconProps = { className?: string };

/** Znak marki: dwie kolumny, druga w kolorze miedzi. */
export function Mark({ className, white = false }: IconProps & { white?: boolean }) {
  const ink = white ? '#fff' : 'currentColor';
  return (
    <svg viewBox="0 0 64 64" className={className} aria-hidden="true">
      <rect x="10" y="8" width="11" height="48" fill={ink} />
      <rect x="10" y="8" width="44" height="11" fill={ink} />
      <rect x="10" y="27" width="30" height="11" fill={ink} />
      <rect x="43" y="27" width="11" height="29" fill="#BE6A2E" />
    </svg>
  );
}

function Stroke({ children, className }: IconProps & { children: React.ReactNode }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="square"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

export function Check({ className }: IconProps) {
  return (
    <Stroke className={className}>
      <path d="M4 12.5l5 5 11-11" strokeWidth="1.8" />
    </Stroke>
  );
}

export function Arrow({ className }: IconProps) {
  return (
    <Stroke className={className}>
      <path d="M4.5 12h14M13 6.5L18.5 12 13 17.5" strokeWidth="1.7" />
    </Stroke>
  );
}

export function User({ className }: IconProps) {
  return (
    <Stroke className={className}>
      <circle cx="12" cy="8" r="3.8" />
      <path d="M4.5 20.5c0-4.1 3.4-7.5 7.5-7.5s7.5 3.4 7.5 7.5" />
    </Stroke>
  );
}

export function Office({ className }: IconProps) {
  return (
    <Stroke className={className}>
      <rect x="3.5" y="7.5" width="7" height="13" />
      <rect x="13.5" y="3.5" width="7" height="17" />
      <path d="M6 11h2M6 15h2M16 7h2M16 11h2M16 15h2" />
    </Stroke>
  );
}

export function Plus({ className }: IconProps) {
  return (
    <Stroke className={className}>
      <path d="M12 4.5v15M4.5 12h15" />
    </Stroke>
  );
}

export function Warn({ className }: IconProps) {
  return (
    <Stroke className={className}>
      <path d="M12 3.5l9 17H3z" strokeWidth="1.7" />
      <path d="M12 9v5M12 17h.01" strokeWidth="1.7" />
    </Stroke>
  );
}

export function Info({ className }: IconProps) {
  return (
    <Stroke className={className}>
      <circle cx="12" cy="12" r="8.5" strokeWidth="1.7" />
      <path d="M12 11v6M12 7.5h.01" strokeWidth="1.7" />
    </Stroke>
  );
}

export function Error({ className }: IconProps) {
  return (
    <Stroke className={className}>
      <circle cx="12" cy="12" r="8.5" strokeWidth="1.7" />
      <path d="M8.5 8.5l7 7M15.5 8.5l-7 7" strokeWidth="1.7" />
    </Stroke>
  );
}

export function Lock({ className }: IconProps) {
  return (
    <Stroke className={className}>
      <rect x="4.5" y="10.5" width="15" height="10" />
      <path d="M8 10.5V7.5a4 4 0 0 1 8 0v3" />
    </Stroke>
  );
}

export function Shield({ className }: IconProps) {
  return (
    <Stroke className={className}>
      <path d="M12 3.5l8 4v5c0 4.2-3.2 7.4-8 8.5-4.8-1.1-8-4.3-8-8.5v-5z" />
      <path d="M9 12l2.2 2.2L15.5 10" />
    </Stroke>
  );
}

/*
 * Ikony bocznej nawigacji aplikacji — z projektu „Aplikacja web”. Rysunek jest kreską
 * o stałej grubości, bez wypełnień: w pasku nawigacji ikona ma odróżniać pozycje od siebie,
 * a nie przyciągać wzrok mocniej niż podpis obok.
 */

export function Dashboard({ className }: IconProps) {
  return (
    <Stroke className={className}>
      <rect x="3.5" y="3.5" width="7" height="7" />
      <rect x="13.5" y="3.5" width="7" height="7" />
      <rect x="3.5" y="13.5" width="7" height="7" />
      <rect x="13.5" y="13.5" width="7" height="7" />
    </Stroke>
  );
}

export function Invoice({ className }: IconProps) {
  return (
    <Stroke className={className}>
      <path d="M5.5 2.5h13v19l-3.25-2-3.25 2-3.25-2-3.25 2z" />
      <path d="M9 8h6M9 12h6M9 16h3" />
    </Stroke>
  );
}

export function Cost({ className }: IconProps) {
  return (
    <Stroke className={className}>
      <rect x="3.5" y="4.5" width="17" height="15" />
      <path d="M3.5 9h17M7.5 14h5" />
    </Stroke>
  );
}

export function Book({ className }: IconProps) {
  return (
    <Stroke className={className}>
      <rect x="3.5" y="4.5" width="17" height="15" />
      <path d="M12 4.5v15M3.5 9h17" />
    </Stroke>
  );
}

export function Users({ className }: IconProps) {
  return (
    <Stroke className={className}>
      <circle cx="9" cy="8" r="3.5" />
      <path d="M3 20.5c0-3.3 2.7-6 6-6s6 2.7 6 6" />
      <path d="M16 5.2a3.5 3.5 0 0 1 0 5.6M18 14.9c1.8.9 3 2.7 3 5.6" />
    </Stroke>
  );
}

export function Doc({ className }: IconProps) {
  return (
    <Stroke className={className}>
      <path d="M5.5 2.5h9l4 4v15h-13z" />
      <path d="M14 2.5V7h4.5M8.5 12h7M8.5 16h4" />
    </Stroke>
  );
}

export function Goods({ className }: IconProps) {
  return (
    <Stroke className={className}>
      <path d="M3.5 7.5L12 3l8.5 4.5v9L12 21l-8.5-4.5z" />
      <path d="M3.5 7.5L12 12l8.5-4.5M12 12v9" />
    </Stroke>
  );
}

export function Gear({ className }: IconProps) {
  return (
    <Stroke className={className}>
      <circle cx="12" cy="12" r="3.2" />
      <path d="M12 2.5v3M12 18.5v3M2.5 12h3M18.5 12h3M5.2 5.2l2.1 2.1M16.7 16.7l2.1 2.1M18.8 5.2l-2.1 2.1M7.3 16.7l-2.1 2.1" />
    </Stroke>
  );
}

export function Fee({ className }: IconProps) {
  return (
    <Stroke className={className}>
      <rect x="3.5" y="5.5" width="17" height="13" />
      <path d="M3.5 10h17M7 14.5h4" />
    </Stroke>
  );
}

export function Asset({ className }: IconProps) {
  return (
    <Stroke className={className}>
      <rect x="3.5" y="9.5" width="17" height="11" />
      <path d="M7 9.5V5.5h10v4M9.5 14h5" />
    </Stroke>
  );
}

export function Balance({ className }: IconProps) {
  return (
    <Stroke className={className}>
      <path d="M12 3.5v17M4.5 8h15" />
      <path d="M4.5 8l-2 6h4zM19.5 8l-2 6h4z" />
    </Stroke>
  );
}

export function Calendar({ className }: IconProps) {
  return (
    <Stroke className={className}>
      <rect x="3.5" y="5.5" width="17" height="15" />
      <path d="M3.5 10h17M8 3v4M16 3v4" />
    </Stroke>
  );
}

export function Stock({ className }: IconProps) {
  return (
    <Stroke className={className}>
      <rect x="3.5" y="8.5" width="17" height="12" />
      <path d="M3.5 8.5L6 3.5h12l2.5 5M12 8.5v12" />
    </Stroke>
  );
}

export function Chevron({ className }: IconProps) {
  return (
    <Stroke className={className}>
      <path d="M8 10l4 4 4-4" />
    </Stroke>
  );
}

/** Kosz: „usuń". Rysowany, bo czynność jest jedna, a jej nazwa („Usuń") zajmuje w wierszu więcej
 *  miejsca niż kolumna z kwotą, którą przykleiona kolumna czynności zasłania. */
export function Trash({ className }: IconProps) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 7h16" />
      <path d="M10 11v6M14 11v6" />
      <path d="M6 7l1 12a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-12" />
      <path d="M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
    </svg>
  );
}

export function Download({ className }: IconProps) {
  return (
    <Stroke className={className}>
      <path d="M12 3.5v11M7.5 10L12 14.5 16.5 10M4 20.5h16" />
    </Stroke>
  );
}

export function Upload({ className }: IconProps) {
  return (
    <Stroke className={className}>
      <path d="M12 20.5v-11M7.5 14L12 9.5 16.5 14M4 3.5h16" />
    </Stroke>
  );
}

export function Search({ className }: IconProps) {
  return (
    <Stroke className={className}>
      <circle cx="10.5" cy="10.5" r="6.5" />
      <path d="M15.5 15.5l5 5" />
    </Stroke>
  );
}
